import { useEffect } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { getLotusFuneralHomeAccess } from '@/api/lotusFuneralHomeLimit'
import { editorLotusFuneralHomeAccessAtom } from '@/atoms/editorTextBoxAtoms'
import { orderAtom } from '@/atoms/printPackageAtoms'
import { logger, EventCategory } from '@/utils/logger'

/** The funeral home of the order the editor has open, or null. */
const useEditorOrderFuneralHomeId = (): string | null => {
  // The atom is declared in a JavaScript module, so its value arrives untyped.
  const order = useRecoilValue(orderAtom) as {
    funeral_home_id?: unknown
  } | null
  const id = order?.funeral_home_id
  return typeof id === 'string' && id !== '' ? id : null
}

/**
 * Reads, once per editor load, whether Lotus text boxes are offered for the
 * open order's funeral home, into `editorLotusFuneralHomeAccessAtom`. The
 * answer is cleared while the read runs and stays cleared when it fails, so
 * `useEditorLotusAvailable` answers false until a read says otherwise. The
 * editor mounts it once.
 */
export const useEditorLotusAvailabilityLoad = () => {
  const funeralHomeId = useEditorOrderFuneralHomeId()
  const setAccess = useSetRecoilState(editorLotusFuneralHomeAccessAtom)

  useEffect(() => {
    setAccess(null)
    if (funeralHomeId === null) return
    let current = true
    getLotusFuneralHomeAccess(funeralHomeId)
      .then(({ data }) => {
        if (current)
          setAccess({ funeralHomeId, allowed: data?.allowed === true })
      })
      .catch((error: unknown) => {
        logger.info({
          message: 'editor_lotus_funeral_home_access_read_failed',
          category: EventCategory.API,
          data: {
            funeralHomeId,
            status:
              (error as { response?: { status?: number } })?.response?.status ??
              null,
          },
        })
      })
    return () => {
      current = false
    }
  }, [funeralHomeId, setAccess])
}

/**
 * Whether Lotus text boxes are offered for the open order's funeral home: the
 * Lotus limit is off, or it lists the funeral home. False while that is
 * unknown (before the editor load's read answers, after it fails, or with no
 * order open), and for an answer about another funeral home.
 */
export const useEditorLotusAvailable = (): boolean => {
  const funeralHomeId = useEditorOrderFuneralHomeId()
  const access = useRecoilValue(editorLotusFuneralHomeAccessAtom)
  return (
    funeralHomeId !== null &&
    access?.funeralHomeId === funeralHomeId &&
    access.allowed
  )
}
